import { queryClient, queryKeys } from '@/lib/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { teamsService } from '../api/teamsService';
import type { AddTeamMemberInput, CreateTeamInput, UpdateTeamInput } from '../types/team';

export function useTeams() {
  return useQuery({
    queryKey: queryKeys.teams.lists(),
    queryFn: () => teamsService.listTeams(),
  });
}

export function useTeam(id: number | null) {
  return useQuery({
    queryKey: queryKeys.teams.detail(String(id)),
    queryFn: () => teamsService.getTeam(id!),
    enabled: id !== null,
  });
}

export function useCreateTeam() {
  return useMutation({
    mutationFn: (data: CreateTeamInput) => teamsService.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      resourceName: 'Team',
      action: 'create',
    },
  });
}

export function useUpdateTeam(id: number) {
  return useMutation({
    mutationFn: (data: UpdateTeamInput) => teamsService.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(id)) });
    },
    meta: {
      resourceName: 'Team',
      action: 'update',
    },
  });
}

export function useDeleteTeam() {
  return useMutation({
    mutationFn: (id: number) => teamsService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      resourceName: 'Team',
      action: 'delete',
    },
  });
}

export function useAddTeamMember(teamId: number) {
  return useMutation({
    mutationFn: (data: AddTeamMemberInput) => teamsService.addTeamMember(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(teamId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      successTitle: 'Member Added',
      successMessage: 'Team member added successfully',
    },
  });
}

export function useRemoveTeamMember(teamId: number) {
  return useMutation({
    mutationFn: ({ userId, newTeamLeadId }: { userId: number; newTeamLeadId?: number }) =>
      teamsService.removeTeamMember(teamId, userId, newTeamLeadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(String(teamId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      successTitle: 'Member Removed',
      successMessage: 'Team member removed successfully',
    },
  });
}
